import { useState, useEffect, useMemo } from "react";
import { format, eachDayOfInterval, getDay, isFirstDayOfMonth, startOfMonth, subMonths, parseISO, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { DEMO_GRID_CONFIG } from "./constants";

// Demo habits with chaotic, realistic human patterns - showing Apr-Dec 2023 only
const DEMO_HABITS = [
  {
    id: "1",
    name: "Exercise",
    color: { filled: "#22c55e", empty: "#f3f4f6" },
    completedDates: [
      // December 2023 - Holiday chaos, barely any exercise
      "2023-12-02", "2023-12-09", "2023-12-23", "2023-12-30",
      // November 2023 - Random bursts
      "2023-11-01", "2023-11-04", "2023-11-05", "2023-11-11", "2023-11-18", "2023-11-19", "2023-11-25", "2023-11-26", "2023-11-28",
      // October 2023 - Solid month 
      "2023-10-02", "2023-10-04", "2023-10-07", "2023-10-09", "2023-10-11", "2023-10-14", "2023-10-16", "2023-10-18", "2023-10-21", "2023-10-23", "2023-10-25", "2023-10-28", "2023-10-30",
      // September 2023 - Back to school motivation crash
      "2023-09-03", "2023-09-10", "2023-09-17", "2023-09-24",
      // August 2023 - Vacation disruption, then comeback
      "2023-08-01", "2023-08-02", "2023-08-19", "2023-08-20", "2023-08-21", "2023-08-26", "2023-08-27", "2023-08-28", "2023-08-31",
      // July 2023 - Summer inconsistency
      "2023-07-04", "2023-07-09", "2023-07-15", "2023-07-22", "2023-07-29",
      // June 2023 - Random good streak
      "2023-06-03", "2023-06-04", "2023-06-05", "2023-06-06", "2023-06-10", "2023-06-17", "2023-06-24",
      // May 2023 - Motivation dip
      "2023-05-07", "2023-05-14", "2023-05-28",
      // April 2023 - Starting fresh with mixed results
      "2023-04-02", "2023-04-05", "2023-04-08", "2023-04-15", "2023-04-22", "2023-04-29"
    ]
  },
  {
    id: "2",
    name: "Read",
    color: { filled: "#3b82f6", empty: "#f3f4f6" },
    completedDates: [
      // December 2023 - Reading slump during holidays
      "2023-12-01", "2023-12-05", "2023-12-12", "2023-12-18", "2023-12-26", "2023-12-30",
      // November 2023 - Cozy reading month
      "2023-11-01", "2023-11-02", "2023-11-04", "2023-11-06", "2023-11-08", "2023-11-10", "2023-11-12", "2023-11-14", "2023-11-16", "2023-11-18", "2023-11-20", "2023-11-22", "2023-11-24", "2023-11-26", "2023-11-28", "2023-11-30",
      // October 2023 - Sporadic but decent
      "2023-10-02", "2023-10-05", "2023-10-08", "2023-10-11", "2023-10-13", "2023-10-16", "2023-10-19", "2023-10-22", "2023-10-25", "2023-10-28", "2023-10-31",
      // September 2023 - Busy month, reading suffered
      "2023-09-03", "2023-09-10", "2023-09-17", "2023-09-24",
      // August 2023 - Beach reading + total slump mid-month
      "2023-08-01", "2023-08-02", "2023-08-03", "2023-08-04", "2023-08-22", "2023-08-23", "2023-08-24", "2023-08-28", "2023-08-30",
      // July 2023 - Vacation reading binge
      "2023-07-01", "2023-07-02", "2023-07-03", "2023-07-04", "2023-07-05", "2023-07-06", "2023-07-07", "2023-07-08", "2023-07-16", "2023-07-23", "2023-07-30",
      // June 2023 - Random pattern
      "2023-06-04", "2023-06-08", "2023-06-12", "2023-06-15", "2023-06-19", "2023-06-23", "2023-06-27",
      // May 2023 - Trying to build habit
      "2023-05-02", "2023-05-05", "2023-05-09", "2023-05-13", "2023-05-16", "2023-05-20", "2023-05-24", "2023-05-27", "2023-05-31",
      // April 2023 - Starting with enthusiasm
      "2023-04-01", "2023-04-03", "2023-04-05", "2023-04-08", "2023-04-10", "2023-04-12", "2023-04-15", "2023-04-18", "2023-04-21", "2023-04-24", "2023-04-27", "2023-04-30"
    ]
  },
  {
    id: "3",
    name: "Meditation",
    color: { filled: "#8b5cf6", empty: "#f3f4f6" },
    completedDates: [
      // December 2023 - Stress relief during holidays
      "2023-12-05", "2023-12-12", "2023-12-19", "2023-12-26",
      // November 2023 - Hit or miss
      "2023-11-03", "2023-11-08", "2023-11-13", "2023-11-18", "2023-11-23", "2023-11-28",
      // October 2023 - Good streak broken
      "2023-10-01", "2023-10-02", "2023-10-03", "2023-10-04", "2023-10-07", "2023-10-14", "2023-10-21", "2023-10-28",
      // September 2023 - Completely forgot about it
      "2023-09-10", "2023-09-24",
      // August 2023 - Vacation meditation
      "2023-08-06", "2023-08-13", "2023-08-20", "2023-08-27",
      // July 2023 - Summer mindfulness
      "2023-07-02", "2023-07-09", "2023-07-16", "2023-07-23", "2023-07-30",
      // June 2023 - Starting to take it seriously
      "2023-06-04", "2023-06-11", "2023-06-18", "2023-06-25",
      // May 2023 - Sporadic attempts
      "2023-05-07", "2023-05-21",
      // April 2023 - First attempts
      "2023-04-09", "2023-04-23"
    ]
  },
  {
    id: "4", 
    name: "Water Intake",
    color: { filled: "#06b6d4", empty: "#f3f4f6" },
    completedDates: [
      // December 2023 - Holiday dehydration
      "2023-12-01", "2023-12-03", "2023-12-07", "2023-12-11", "2023-12-15", "2023-12-19", "2023-12-27", "2023-12-30",
      // November 2023 - Decent hydration
      "2023-11-01", "2023-11-02", "2023-11-04", "2023-11-06", "2023-11-08", "2023-11-10", "2023-11-12", "2023-11-14", "2023-11-16", "2023-11-18", "2023-11-20", "2023-11-22", "2023-11-24", "2023-11-26", "2023-11-28", "2023-11-30",
      // October 2023 - Forgot my water bottle a lot
      "2023-10-02", "2023-10-05", "2023-10-08", "2023-10-12", "2023-10-15", "2023-10-18", "2023-10-22", "2023-10-25", "2023-10-28", "2023-10-31",
      // September 2023 - Back to work, forgot to hydrate
      "2023-09-01", "2023-09-05", "2023-09-12", "2023-09-19", "2023-09-26",
      // August 2023 - Hot summer, drinking lots
      "2023-08-01", "2023-08-02", "2023-08-03", "2023-08-04", "2023-08-05", "2023-08-07", "2023-08-08", "2023-08-09", "2023-08-11", "2023-08-12", "2023-08-14", "2023-08-15", "2023-08-16", "2023-08-18", "2023-08-19", "2023-08-21", "2023-08-22", "2023-08-24", "2023-08-25", "2023-08-27", "2023-08-28", "2023-08-30", "2023-08-31",
      // July 2023 - Vacation hydration mixed
      "2023-07-01", "2023-07-03", "2023-07-05", "2023-07-08", "2023-07-10", "2023-07-13", "2023-07-15", "2023-07-18", "2023-07-20", "2023-07-23", "2023-07-25", "2023-07-28", "2023-07-30",
      // June 2023 - Random pattern
      "2023-06-02", "2023-06-05", "2023-06-08", "2023-06-11", "2023-06-14", "2023-06-17", "2023-06-20", "2023-06-23", "2023-06-26", "2023-06-29",
      // May 2023 - Trying to build habit
      "2023-05-01", "2023-05-04", "2023-05-07", "2023-05-10", "2023-05-13", "2023-05-16", "2023-05-19", "2023-05-22", "2023-05-25", "2023-05-28", "2023-05-31",
      // April 2023 - Spring hydration start
      "2023-04-03", "2023-04-06", "2023-04-09", "2023-04-12", "2023-04-15", "2023-04-18", "2023-04-21", "2023-04-24", "2023-04-27", "2023-04-30"
    ]
  },
  {
    id: "5",
    name: "Journal",
    color: { filled: "#f59e0b", empty: "#f3f4f6" },
    completedDates: [
      // December 2023 - Year end chaos
      "2023-12-10", "2023-12-24", "2023-12-31",
      // November 2023 - Gratitude month
      "2023-11-05", "2023-11-12", "2023-11-19", "2023-11-26",
      // October 2023 - Sporadic
      "2023-10-08", "2023-10-22",
      // September 2023 - Back to school thoughts
      "2023-09-03", "2023-09-17",
      // August 2023 - Summer reflections
      "2023-08-06", "2023-08-20",
      // July 2023 - Vacation journaling
      "2023-07-02", "2023-07-16", "2023-07-30",
      // June 2023 - Random bursts
      "2023-06-11", "2023-06-25",
      // May 2023 - Spring thoughts
      "2023-05-07", "2023-05-21",
      // April 2023 - Starting the habit
      "2023-04-09", "2023-04-23"
    ]
  }
];

// Use a fixed reference date for the demo - showing Apr-Dec 2023
const DEMO_END_DATE = parseISO("2023-12-31");

export default function DemoHabitGrid() {
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const isMobile = useIsMobile();
  
  // Calculate total completed days
  const totalCompletedDays = useMemo(() => {
    const allDates = new Set<string>();
    DEMO_HABITS.forEach(habit => {
      habit.completedDates.forEach(date => allDates.add(date));
    });
    return allDates.size;
  }, []);

  // Generate the grid data
  useEffect(() => {
    const endDate = DEMO_END_DATE;
    const startDate = startOfMonth(subMonths(endDate, DEMO_GRID_CONFIG.MONTHS_TO_SHOW - 1));
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date);
      
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      for (let i = 0; i < 7; i++) {
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, []);

  // Get count of habits completed on a specific date
  const getHabitsCompletedCount = (date: Date | null): number => {
    if (!date) return 0;
    const dateString = format(date, 'yyyy-MM-dd');
    return DEMO_HABITS.filter(habit => habit.completedDates.includes(dateString)).length;
  };

  // Get color for a cell based on habits completed with intensity
  const getCellColor = (date: Date | null): string => {
    if (!date) return 'transparent';
    
    const dateString = format(date, 'yyyy-MM-dd');
    const completedHabits = DEMO_HABITS.filter(habit => 
      habit.completedDates.includes(dateString)
    );
    
    const completedCount = completedHabits.length;
    
    // No habits completed - use empty color
    if (completedCount === 0) return 'hsl(var(--habit-empty))';
    
    // Use the app's primary purple color as the base for intensity
    // Grading: 1 habit, 2 habits, 3+ (3–4), 5+ (5–6), 7+
    let lightness: number;
    if (completedCount === 1) {
      lightness = 75; // Light for 1 habit
    } else if (completedCount === 2) {
      lightness = 65; // Medium-light for 2 habits
    } else if (completedCount === 3 || completedCount === 4) {
      lightness = 55; // Medium for 3+ habits (3–4 completed)
    } else if (completedCount === 5 || completedCount === 6) {
      lightness = 45; // Medium-dark for 5+ habits (5–6 completed)
    } else {
      lightness = 35; // Dark for 7+ habits
    }
    
    return `hsl(266, 70%, ${lightness}%)`;
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const cellSize = isMobile ? DEMO_GRID_CONFIG.CELL_SIZE.MOBILE : DEMO_GRID_CONFIG.CELL_SIZE.DESKTOP;
  const cellGap = isMobile ? DEMO_GRID_CONFIG.CELL_GAP.MOBILE : DEMO_GRID_CONFIG.CELL_GAP.DESKTOP;
  const weekdayColumnWidth = isMobile ? DEMO_GRID_CONFIG.WEEKDAY_COLUMN.MOBILE : DEMO_GRID_CONFIG.WEEKDAY_COLUMN.DESKTOP;
  const rowHeight = isMobile ? DEMO_GRID_CONFIG.ROW_HEIGHT.MOBILE : DEMO_GRID_CONFIG.ROW_HEIGHT.DESKTOP;

  return (
    <div className="demo-habit-grid overflow-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium">Combined Progress</h3>
          <span className="text-sm text-muted-foreground">
            {totalCompletedDays} days tracked
          </span>
        </div>
      </div>
      
      <div className="overflow-x-auto w-full">
        <div className="min-w-max">
          {/* Month labels */}
          <div className="flex h-6 relative mb-1">
            <div className={`${weekdayColumnWidth} mr-2`}></div>
            <div className="flex-1 relative">
              {months.map((month, i) => (
                <div 
                  key={i} 
                  className="absolute text-xs text-muted-foreground"
                  style={{ left: `${month.index * (isMobile ? 15 : 20)}px` }}
                >
                  {month.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex">
            {/* Weekday labels */}
            <div className={`flex flex-col ${weekdayColumnWidth} mr-2`}>
              {weekdays.map((day, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center text-xs text-muted-foreground leading-none mb-1"
                  style={{ height: rowHeight }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Grid cells */}
            <div className="flex flex-col">
              {gridData.map((row, rowIndex) => (
                <div 
                  key={`row-${rowIndex}`} 
                  className={`flex items-center ${cellGap} mb-1`} 
                  style={{ height: rowHeight }}
                >
                  {row.map((date, colIndex) => {
                    const habitsCount = getHabitsCompletedCount(date);
                    const dateStr = date ? format(date, 'MMMM d, yyyy') : '';
                    const completionStatus = habitsCount > 0 
                      ? `${habitsCount} habit${habitsCount > 1 ? 's' : ''} completed` 
                      : 'No habits completed';
                    const glowEffect = habitsCount > 0 ? "shadow-[0_0_5px_0px_rgba(255,255,255,0.3)] dark:shadow-[0_0_8px_0px_rgba(255,255,255,0.15)]" : "";
                    const isTodayDate = date ? isToday(date) : false;
                    const todayBorder = isTodayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
                    
                    return (
                      <div
                        key={`cell-${rowIndex}-${colIndex}`}
                        className={`${cellSize} rounded-sm transition-all hover:scale-125 hover:z-10 hover:shadow-md cursor-pointer grid-cell ${glowEffect} ${todayBorder}`}
                        style={{ 
                          backgroundColor: getCellColor(date),
                          visibility: date ? 'visible' : 'hidden'
                        }}
                        title={date ? `${dateStr} - ${completionStatus}${isTodayDate ? ' (Today)' : ''}` : ''}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 75%)' }}
          />
          <span className="text-sm">1 habit</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 65%)' }}
          />
          <span className="text-sm">2 habits</span>
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 55%)' }}
          />
          <span className="text-sm">3+ habits</span>
        </div>
        <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 45%)' }}
            />
          <span className="text-sm">5+ habits</span>
          </div>
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'hsl(266, 70%, 35%)' }}
          />
          <span className="text-sm">7+ habits</span>
        </div>
      </div>
    </div>
  );
} 